import { UntypedFormArray, ValidatorFn } from '@angular/forms';
import { FilteredAccount, ProductType } from '@interfaces/models/client-accounts.interface';
import { JointGiftingApprovalDBPayloadFilteredAccount } from '@shared/models';
import { formatBalance } from '@sharedUtils/money.helpers';
import { ISelectedAccount } from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';
import * as dayjs from 'dayjs';
export { formatBalance } from '@sharedUtils/money.helpers';

type AccountFilterFunc = (account?: FilteredAccount) => boolean;
const isBrokerageAccount: AccountFilterFunc = (account: FilteredAccount): boolean =>
  account.productType === ProductType.BROKERAGE;
const isAccount: AccountFilterFunc = (): boolean => true;

const formatAccount = (account: FilteredAccount, multiOwner: boolean): FilteredAccount => ({
  ...account,
  positions: account.positions?.map((position) => ({
    ...position,
    formattedBalance: formatBalance(position.balance),
  })),
  formattedAccountName: getAccountName(account, multiOwner),
  balanceDisplay: formatBalance(account.balance),
  dateDisplay: dayjs(account.balanceAsOfDateTime).format('MM/DD/YYYY'),
});

export const formatShareQuantity = (n: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(+n);
};

/**
 * Filters an account list and formats the individual accounts.
 * @function
 * @param {FilteredAccount[]} displayedAccountsArray - The account list.
 * @param {boolean} multiOwner - Single owner or multiple owners.
 * @param {AccountFilterFunc} filterFunc - Optional filtering function reference. By default no account is filtered.
 */
export const getFormattedAndFilteredAccounts = (
  displayedAccountsArray: FilteredAccount[],
  multiOwner: boolean,
  filterFunc: AccountFilterFunc = isAccount,
): FilteredAccount[] =>
  displayedAccountsArray
    .filter(filterFunc)
    .sort((first) => (first.isBrokerage ? -1 : 1))
    .map((account) => formatAccount(account, multiOwner));

export const getFormattedBrokerageAccounts = (
  displayedAccountsArray: FilteredAccount[],
): FilteredAccount[] =>
  getFormattedAndFilteredAccounts(displayedAccountsArray, false, isBrokerageAccount);

export const getFormattedAccounts = (
  displayedAccountsArray: FilteredAccount[],
  multiOwner = false,
): FilteredAccount[] => getFormattedAndFilteredAccounts(displayedAccountsArray, multiOwner);

export const getAccountName = (
  account: Pick<FilteredAccount, 'accountName' | 'accountNumber' | 'isBrokerage'>,
  multiOwner: boolean,
): string => {
  if (!account || !account.accountName) return null;

  const { isBrokerage, accountName, accountNumber } = account;
  const splitName = account.accountName.split(',');

  const prefix = multiOwner ? `${splitName.join(' &')}` : accountName;
  return isBrokerage ? prefix : `${prefix} \u2014 ${accountNumber}`;
};

export const getSelectedAccountPayload = (
  account: FilteredAccount | JointGiftingApprovalDBPayloadFilteredAccount,
  index,
): ISelectedAccount => {
  return (
    index < 0
      ? {
          accountData: {} as FilteredAccount,
          accountNumber: 'newAccount',
          accountType: null,
          index: index.toString(),
        }
      : {
          accountData: account,
          accountNumber: account.accountNumber,
          accountType: account.accountType,
          index: index?.toString(),
        }
  ) as ISelectedAccount;
};

export const eligibleAccountValidator: ValidatorFn = (formArray: UntypedFormArray) => {
  if (formArray.length > 0) {
    for (const control of formArray.controls) {
      if (control.value) {
        return null;
      }
    }
  }
  return { accountsNotSelected: true };
};
